import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import type { z } from 'zod';
import { zodRemoveNullable } from '@/lib/zod-helper';
import { fileManagerApi } from '@/services';
import type { ApiResponse } from '@/types/api-types';
import type { QueryOptions } from '@/types/tanstack-query-options';
import type { paths } from '~/generated/file-manager/api-schema';
import { fileInfoResSchema } from '~/generated/file-manager/zod-schema';

const PATH_VAR = '{id}';
const API_PATH = `/v1/fileManager/queryFilesInfo/${PATH_VAR}` as const;
export const QUERY_FILE_INFO_KEY = API_PATH;

type APIParams = string;
type APIResponse = ApiResponse<paths, typeof API_PATH>;

const apiResponseSchema = zodRemoveNullable(fileInfoResSchema, {
  excludeMask: { modifyDatetime: true },
});
type APIResponseData = z.infer<typeof apiResponseSchema>;

const queryFn = async (fileId: APIParams, signal: AbortSignal) => {
  const { data: resp } = await fileManagerApi.get<APIResponse>(
    API_PATH.replace(PATH_VAR, fileId),
    { signal },
  );

  return apiResponseSchema.parse(resp.data);
};

export const getQueryFileInfoByIdOptions = (fileId: string | undefined) => {
  return queryOptions({
    queryKey: [API_PATH, fileId] as const,
    queryFn: !fileId
      ? skipToken
      : async ({ signal }) => await queryFn(fileId, signal),
  });
};

export const useQueryFileInfoById = (
  fileId: string | undefined,
  options?: QueryOptions<APIResponseData>,
) => {
  return useQuery({
    queryKey: [API_PATH, fileId],
    queryFn: !fileId
      ? skipToken
      : async ({ signal }) => await queryFn(fileId, signal),
    ...options,
  });
};
